import { supabase } from '../lib/supabase';

export const appointmentService = {
  /**
   * Create a new appointment
   */
  async createAppointment(appointmentData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case
      const dbData = {
        user_id: user?.id,
        helper_id: appointmentData?.helperId,
        session_type: appointmentData?.sessionType,
        appointment_date: appointmentData?.appointmentDate,
        appointment_time: appointmentData?.appointmentTime,
        duration: appointmentData?.duration,
        topic: appointmentData?.topic,
        location: appointmentData?.location,
        preparation_notes: appointmentData?.preparationNotes,
        status: 'scheduled',
        booking_charge_amount: appointmentData?.bookingChargeAmount ? parseFloat(appointmentData?.bookingChargeAmount) : null,
        booking_charge_currency: appointmentData?.bookingChargeCurrency || null,
        stripe_payment_intent_id: appointmentData?.stripePaymentIntentId || null
      };

      const { data, error } = await supabase?.from('appointments')?.insert(dbData)?.select()?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      const appointment = {
        id: data?.id,
        userId: data?.user_id,
        helperId: data?.helper_id,
        sessionType: data?.session_type,
        appointmentDate: data?.appointment_date,
        appointmentTime: data?.appointment_time,
        duration: data?.duration,
        topic: data?.topic,
        location: data?.location,
        preparationNotes: data?.preparation_notes,
        status: data?.status,
        bookingChargeAmount: data?.booking_charge_amount,
        bookingChargeCurrency: data?.booking_charge_currency,
        stripePaymentIntentId: data?.stripe_payment_intent_id,
        createdAt: data?.created_at
      };

      return { data: appointment, error: null };
    } catch (error) {
      console.error('Create appointment error:', error);
      return { data: null, error };
    }
  },

  /**
   * Process booking payment via Stripe
   */
  async processBookingPayment(helperId, appointmentId, amount, currency) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user profile for Stripe customer info
      const { data: userProfile } = await supabase
        ?.from('user_profiles')
        ?.select('stripe_customer_id, email, full_name')
        ?.eq('id', user?.id)
        ?.single();

      const customerInfo = {
        userId: user?.id,
        email: userProfile?.email || user?.email,
        name: userProfile?.full_name || user?.email?.split('@')?.[0],
        stripeCustomerId: userProfile?.stripe_customer_id
      };

      // Call Edge Function to create payment intent
      const { data, error } = await supabase?.functions?.invoke('create-booking-payment', {
        body: {
          helperId,
          appointmentId,
          amount,
          currency,
          customerInfo
        }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Process booking payment error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get user's appointments
   */
  async getUserAppointments(status = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase?.from('appointments')?.select(`
          *,
          helpers (
            name,
            type_label,
            profile_image,
            profile_image_alt,
            rating
          )
        `)?.eq('user_id', user?.id)?.order('appointment_date', { ascending: true })?.order('appointment_time', { ascending: true });

      if (status) {
        query = query?.eq('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase
      const appointments = data?.map(appt => ({
        id: appt?.id,
        userId: appt?.user_id,
        helperId: appt?.helper_id,
        helperName: appt?.helpers?.name,
        helperRole: appt?.helpers?.type_label,
        helperImage: appt?.helpers?.profile_image,
        helperImageAlt: appt?.helpers?.profile_image_alt,
        sessionType: appt?.session_type,
        date: appt?.appointment_date,
        time: appt?.appointment_time,
        duration: appt?.duration,
        topic: appt?.topic,
        location: appt?.location,
        preparationNotes: appt?.preparation_notes,
        status: appt?.status,
        createdAt: appt?.created_at
      })) || [];

      return { data: appointments, error: null };
    } catch (error) {
      console.error('Get appointments error:', error);
      return { data: [], error };
    }
  },

  /**
   * Get appointment by ID
   */
  async getAppointmentById(appointmentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('appointments')?.select(`
          *,
          helpers (
            name,
            type_label,
            profile_image,
            profile_image_alt,
            rating
          )
        `)?.eq('id', appointmentId)?.eq('user_id', user?.id)?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      const appointment = {
        id: data?.id,
        userId: data?.user_id,
        helperId: data?.helper_id,
        helperName: data?.helpers?.name,
        helperRole: data?.helpers?.type_label,
        helperImage: data?.helpers?.profile_image,
        helperImageAlt: data?.helpers?.profile_image_alt,
        sessionType: data?.session_type,
        date: data?.appointment_date,
        time: data?.appointment_time,
        duration: data?.duration,
        topic: data?.topic,
        location: data?.location,
        preparationNotes: data?.preparation_notes,
        status: data?.status,
        createdAt: data?.created_at
      };

      return { data: appointment, error: null };
    } catch (error) {
      console.error('Get appointment by ID error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update appointment status
   */
  async updateAppointmentStatus(appointmentId, status) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('appointments')?.update({ status })?.eq('id', appointmentId)?.eq('user_id', user?.id)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update appointment status error:', error);
      return { data: null, error };
    }
  },

  /**
   * Cancel appointment
   */
  async cancelAppointment(appointmentId) {
    return this.updateAppointmentStatus(appointmentId, 'cancelled');
  }
};